/**
 * CoralThumb — a coral photo with graceful loading + fallback:
 *   • shows a soft pulsing placeholder until the image decodes (so a slow
 *     load looks intentional, not "missing"),
 *   • retries the preferred photo a couple of times before falling back to
 *     the other one (prevents a transient network blip from permanently
 *     swapping a colony shot for a frag),
 *   • finally shows a "No photo" chip if both fail.
 * Used by the guide cards/list, collection, and the placer dots/library.
 */
import { useEffect, useState } from 'react';

import { colonyPhotoUrl, fragPhotoUrl } from './catalog';
import type { Coral, PlacementPhoto } from './types';

export interface CoralThumbProps {
  readonly coral: Coral;
  readonly prefer?: PlacementPhoto;
  readonly className?: string;
  readonly alt?: string;
}

const MAX_RETRIES = 2;

export function CoralThumb({
  coral,
  prefer = 'colony',
  className,
  alt,
}: CoralThumbProps) {
  const order =
    prefer === 'colony'
      ? [colonyPhotoUrl(coral), fragPhotoUrl(coral)]
      : [fragPhotoUrl(coral), colonyPhotoUrl(coral)];

  const [idx, setIdx] = useState(0);
  const [retries, setRetries] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setIdx(0);
    setRetries(0);
    setLoaded(false);
  }, [coral.id, prefer]);

  const base = order[idx];
  // A retry adds a cache-busting query param so the browser re-requests
  // instead of replaying a failed cache entry; the first (successful) load
  // uses the clean URL so normal HTTP caching still applies.
  const src = base === undefined ? undefined : retries > 0 ? `${base}?r=${retries}` : base;

  const handleError = () => {
    setLoaded(false);
    if (retries < MAX_RETRIES) {
      setRetries((r) => r + 1);
    } else {
      setRetries(0);
      setIdx((i) => i + 1);
    }
  };

  return (
    <span className={`relative block overflow-hidden bg-bg-mid ${className ?? ''}`}>
      {!loaded && (
        <span className="absolute inset-0 animate-pulse bg-surface-glass" aria-hidden="true" />
      )}
      {src === undefined ? (
        <span className="absolute inset-0 flex items-center justify-center text-[10px] text-text-3">
          No photo
        </span>
      ) : (
        <img
          src={src}
          alt={alt ?? coral.name}
          // `pointer-events-none` + `draggable={false}` stop the browser from
          // starting a native image-drag when you press a placement dot — that
          // drag was hijacking the pointer so thumbnail dots couldn't be moved.
          className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover"
          style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.2s ease' }}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      )}
    </span>
  );
}
